import { useCallback, useEffect, useMemo, useReducer, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { normalizeStreamType, orderedUrls, type StationV2, type UrlV2 } from "@orvis/core";
import { trackEvent } from "../../analytics.js";
import { HlsAudioPlayer, HlsVideoPlayer } from "./hls.js";
import { AudioPlayer } from "./audio.js";
import { WebPlayer } from "./web.js";
import { initialPlayback, playbackReducer, reconnectDelay, type PlaybackStatus } from "./playbackPolicy.js";

/**
 * Plays a station, walking its URLs in `orderedUrls()` order and recovering
 * from drops per playbackPolicy.ts. Callers pass `mode="video"` for TV
 * playback or `mode="audio"` for radio; that picks the shape of HLS player.
 *
 * Missing / unrecognized types fall through the same coercion the V2
 * parser uses (`normalizeStreamType` → "WEB").
 */
export function StationPlayer({
  station,
  mode,
  paused = false,
  nativeControls = true,
  onError,
  onElement,
  onActiveUrl,
  failedActions,
}: {
  station: StationV2;
  mode: "video" | "audio";
  paused?: boolean;
  /** Show the browser's own media controls. TV passes false and renders PlayerControls instead. */
  nativeControls?: boolean;
  /** Fires once every URL has failed. */
  onError?: (err: Error) => void;
  onElement?: (el: HTMLMediaElement | null) => void;
  onActiveUrl?: (url: UrlV2 | undefined) => void;
  /** Extra buttons shown next to "Try again" once playback has failed (e.g. report). */
  failedActions?: ReactNode;
}) {
  const urls = useMemo(() => orderedUrls(station.urls), [station]);
  const [state, dispatch] = useReducer(playbackReducer, urls.length, initialPlayback);
  const stationId = station.id ?? station.name;

  // New station → start again from its first URL.
  useEffect(() => dispatch({ type: "reset", total: urls.length }), [urls]);

  // Backoff timer while reconnecting.
  useEffect(() => {
    if (state.status !== "reconnecting") return;
    const t = setTimeout(() => dispatch({ type: "retry" }), reconnectDelay(state.retries));
    return () => clearTimeout(t);
  }, [state.status, state.retries]);

  // Coming back online retries whatever was stuck.
  useEffect(() => {
    const on = () => dispatch({ type: "online" });
    window.addEventListener("online", on);
    return () => window.removeEventListener("online", on);
  }, []);

  useEffect(() => {
    if (state.status === "switching") trackEvent("stream_fallback", { item_id: stationId, url_index: state.urlIndex });
    if (state.status === "reconnecting") trackEvent("stream_reconnect", { item_id: stationId, attempt: state.retries });
    if (state.status === "failed") {
      trackEvent("stream_failed", { item_id: stationId, urls: urls.length });
      onError?.(new Error("all stream urls failed"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.status, state.retries, state.urlIndex]);

  // Track the element's own paused state so the loading spinner doesn't sit
  // over a video that's simply waiting for the user to press play.
  const [el, setEl] = useState<HTMLMediaElement | null>(null);
  const [elPaused, setElPaused] = useState(false);
  const handleElement = useCallback(
    (next: HTMLMediaElement | null) => {
      setEl(next);
      onElement?.(next);
    },
    [onElement]
  );
  useEffect(() => {
    if (!el) return;
    const sync = () => setElPaused(el.paused);
    sync();
    el.addEventListener("pause", sync);
    el.addEventListener("play", sync);
    return () => {
      el.removeEventListener("pause", sync);
      el.removeEventListener("play", sync);
    };
  }, [el]);

  const current = urls[state.urlIndex];
  useEffect(() => onActiveUrl?.(current), [current, onActiveUrl]);

  if (!current) return <PlayerStatus nativeControls={nativeControls} mode={mode} status="failed" onRetry={() => dispatch({ type: "manualRetry" })} />;

  const type = normalizeStreamType(current.type);
  const src = current.value;
  const cb = {
    controls: nativeControls,
    onPlaying: () => dispatch({ type: "playing" }),
    onFatal: (e: { message: string; network: boolean }) => {
      console.warn(`[player] ${station.name} url#${state.urlIndex}: ${e.message}`);
      dispatch({ type: "fatal", network: e.network, offline: typeof navigator !== "undefined" && !navigator.onLine });
    },
    onElement: handleElement,
  };
  const key = `${state.urlIndex}-${state.nonce}`;

  let player: ReactNode;
  if (state.status === "failed") {
    player = null;
  } else if (type === "HLS") {
    player = mode === "video"
      ? <HlsVideoPlayer key={key} src={src} paused={paused} {...cb} />
      : <HlsAudioPlayer key={key} src={src} paused={paused} {...cb} />;
  } else if (type === "MP3" || type === "AAC") {
    player = <AudioPlayer key={key} src={src} paused={paused} {...cb} />;
  } else {
    // WEB or unknown — an iframe embed has no imperative pause/resume control
    // and no error signal, so it never takes part in fallback.
    player = <WebPlayer key={key} src={src} />;
  }

  if (mode === "video") {
    return (
      <div className="relative w-full h-full">
        {player}
        <PlayerStatus
          nativeControls={nativeControls}
          mode={mode}
          status={state.status === "loading" && elPaused ? "playing" : state.status}
          retries={state.retries}
          onRetry={() => dispatch({ type: "manualRetry" })}
          failedActions={failedActions}
        />
      </div>
    );
  }
  return (
    <div className="space-y-1">
      {player}
      <PlayerStatus
          nativeControls={nativeControls}
        mode={mode}
        status={state.status}
        retries={state.retries}
        onRetry={() => dispatch({ type: "manualRetry" })}
        failedActions={failedActions}
      />
    </div>
  );
}

function PlayerStatus({
  mode,
  nativeControls = true,
  status,
  retries = 0,
  onRetry,
  failedActions,
}: {
  mode: "video" | "audio";
  nativeControls?: boolean;
  status: PlaybackStatus;
  retries?: number;
  onRetry: () => void;
  failedActions?: ReactNode;
}) {
  const { t } = useTranslation();
  if (status === "loading") {
    // A quiet spinner. Audio skips it when its native controls already show
    // progress — on TV they're hidden, so it needs one too.
    if (mode === "video") {
      return (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-white/80">
          <Spinner large />
        </div>
      );
    }
    return nativeControls ? null : (
      <div className="flex items-center justify-center text-text/70">
        <Spinner />
      </div>
    );
  }
  let label: string | undefined;
  if (status === "switching") label = t("player_switching");
  else if (status === "reconnecting") label = `${t("player_reconnecting")} (${retries})`;
  else if (status === "offline") label = t("player_offline");
  else if (status === "failed") label = t("player_failed");
  if (!label) return null;

  const body = (
    <div role="status" aria-live="polite" className="flex flex-wrap items-center justify-center gap-2 text-sm">
      {status !== "failed" ? <Spinner /> : null}
      <span>{label}</span>
      {status === "failed" ? (
        <>
          <button
            type="button"
            onClick={onRetry}
            className="px-2 py-1 rounded border border-border hover:border-accent focus-visible:border-accent"
          >
            {t("retry")}
          </button>
          {failedActions}
        </>
      ) : null}
    </div>
  );

  if (mode === "video") {
    return (
      <div
        className={
          "absolute inset-0 flex items-center justify-center p-4 text-white " +
          (status === "failed" ? "bg-black/80" : "bg-black/40 pointer-events-none")
        }
      >
        <div className={status === "failed" ? "pointer-events-auto" : ""}>{body}</div>
      </div>
    );
  }
  return <div className="text-text/70">{body}</div>;
}

function Spinner({ large = false }: { large?: boolean }) {
  return (
    <span
      aria-hidden
      className={
        "inline-block rounded-full border-current border-t-transparent animate-spin " +
        (large ? "h-10 w-10 border-4" : "h-4 w-4 border-2")
      }
    />
  );
}
